"use client";

import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { cn } from "../utils";

export type ClaimDisclaimerProps = {
  disclaimer: string;
  /** Initial open state. Defaults to closed. */
  defaultOpen?: boolean;
  /**
   * When true, the disclaimer is suppressed entirely — used for historic
   * claim steps the user has already moved past, mirroring the previous
   * inline behavior in ClaimWidget.
   */
  historic?: boolean;
  className?: string;
};

/**
 * Collapsible disclaimer for a `show_claim` widget. Renders unobtrusively
 * at the bottom of a step (`*Disclaimer` + chevron). Expanding reveals
 * the full compliance disclaimer text.
 *
 * Lives at group footer — see `ChatMessageGroup.footerItems` — so it
 * docks to the bottom of the step instead of flowing inline with the
 * claim copy.
 */
export function ClaimDisclaimer({
  disclaimer,
  defaultOpen = false,
  historic = false,
  className,
}: ClaimDisclaimerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  if (historic) return null;
  if (!disclaimer) return null;

  return (
    <div
      className={cn("flex flex-col text-text-secondary", className)}
      data-slot="claim-disclaimer"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center justify-between gap-2 border-none bg-transparent p-0 text-xs text-text-secondary cursor-pointer"
      >
        <span>*Disclaimer</span>
        <ChevronDown
          size={14}
          className={cn("shrink-0 transition-transform", open && "rotate-180")}
          strokeWidth={1.5}
          aria-hidden
        />
      </button>
      {open && (
        <div
          id={panelId}
          className="mt-3 flex flex-col gap-4 text-xs leading-[1.4] tracking-[-0.4px] text-text-primary/70"
        >
          <p className="whitespace-pre-line">{disclaimer}</p>
        </div>
      )}
    </div>
  );
}
